import { DatePipe, NgClass } from "@angular/common";
import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeButtonModule, ForgeIconButtonModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { ICalendarDate, PropertyMetadata } from "@tylertech/tyler-api-types";
import moment from "moment";
import { AugmentedModelObject, RepositoryFormData } from "../../interfaces";
import { MetadataEventService, MetadataPropertyService } from "../../services";
import { MetadataCalendarDayComponent } from "./metadataCalendarDay.component";

export interface MetadataCalendarToggleParameter
{
    date: Date;
}

export interface IOrderedCalendarDate extends ICalendarDate
{
    DisplayIndex: number;
    DateKey: string;
    DayTotal: number;
    Key: string;
    StartDate: Date;
}

@Component({
    selector: "metadata-calendar-month",
    templateUrl: "./metadataCalendarMonth.tmpl.html",
    imports: [
        DatePipe,
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        MetadataCalendarDayComponent,
        NgClass,
    ]
})

export class MetadataCalendarMonthComponent implements OnInit, OnChanges
{
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataEventService = inject(MetadataEventService);
    locale = inject(LOCALE_ID);

    @Input() formRepository: RepositoryFormData;
    @Input() propertyMetadata: PropertyMetadata;
    @Input() metadataModel: AugmentedModelObject;
    @Input() model: ICalendarDate[];
    @Input() parent: AugmentedModelObject;
    @Input() viewOnly?: boolean;
    @Input() isDrillIn: boolean;
    @Input() calDate: Date;
    @Input() startDate: Date;
    @Output() private readonly toggleDrillIn = new EventEmitter<MetadataCalendarToggleParameter>();

    public namesOfDays = [
        "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"
    ];

    public displayedWeeks: Array<Date[]> = [];
    private displayedDates: Date[] = [];
    private firstDayOfRange = "calendar-range--start";
    private lastDayOfRange = "calendar-range--end";
    private isInitialized: boolean;
    currentMonthDates: IOrderedCalendarDate[];
    singleMonth: boolean = false;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (!args.Recalculating)
                {
                    self.setCurrentMonthDates();
                    self.setOrderedEvents();
                }
            });
    }

    ngOnInit(): void
    {
        if (!this.calDate)
        {
            this.singleMonth = true;
            const calMetadata = this.propertyMetadata.CalendarMetadata;
            if (!this.startDate)
                this.startDate = calMetadata.StartDateProperty
                    ? new Date(this.metadataModel[calMetadata.StartDateProperty])
                    : new Date(calMetadata.StartDate);
            this.setCalDate();
        }
        this.setDisplayedDates(this.calDate);
        this.setCurrentMonthDates();
        this.isInitialized = true;
    }

    orderedEvents: Record<string, IOrderedCalendarDate[]>;
    private dateKeyCounts: Map<string, number>; // CLOUDIV-30423: Cache DateKey counts for sorting
    private eventByIdCache: Map<any, any>; // CLOUDIV-30423: Cache events by ID for O(1) click lookup

    setOrderedEvents(): void
    {
        // CLOUDIV-30423: Pre-calculate DateKey counts ONCE instead of filtering repeatedly in doSort
        this.dateKeyCounts = new Map<string, number>();
        for (const date of this.currentMonthDates)
        {
            const key = date.DateKey;
            this.dateKeyCounts.set(key, (this.dateKeyCounts.get(key) || 0) + 1);
        }

        // CLOUDIV-30423: Group dates by stringDate ONCE instead of filtering for each displayed date
        const dateGroups = new Map<string, IOrderedCalendarDate[]>();
        for (const date of this.currentMonthDates)
        {
            const dateStr = date["$stringDate"];
            if (!dateGroups.has(dateStr))
            {
                dateGroups.set(dateStr, []);
            }
            dateGroups.get(dateStr).push(date as IOrderedCalendarDate);
        }

        // CLOUDIV-30423: Now just lookup and sort (sorting now uses cached counts)
        this.orderedEvents = {};
        this.displayedDates.forEach(dt =>
        {
            const dateStr = dt.toDateString();
            const events = dateGroups.get(dateStr) || [];
            events.sort((a, b) => this.doSortOptimized(a, b));
            this.orderedEvents[dateStr] = events;
        });
    }

    // CLOUDIV-30423: Optimized version using pre-calculated counts
    doSortOptimized(a: ICalendarDate, b: ICalendarDate): number
    {
        const countA = this.dateKeyCounts.get(a.DateKey) || 0;
        const countB = this.dateKeyCounts.get(b.DateKey) || 0;

        if (countA === countB)
            return a.DateKey.toLowerCase() < b.DateKey.toLowerCase() ? -1 : 1;
        return countA > countB ? -1 : 1;
    }

    doSort(a: ICalendarDate, b: ICalendarDate): number
    {
        const countA = this.currentMonthDates.filter(x => x.DateKey === a.DateKey).length;
        const countB = this.currentMonthDates.filter(x => x.DateKey === b.DateKey).length;

        if (countA === countB)
            return a.DateKey.toLowerCase() < b.DateKey.toLowerCase() ? -1 : 1;
        return countA > countB ? -1 : 1;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!this.isInitialized)
            return;
        if (changes["model"])
            this.setCurrentMonthDates();
    }

    private setDisplayedDates(currentMoment: Date | moment.Moment): void
    {
        this.displayedDates = [];
        const firstOfMonth = moment(currentMoment)
            .startOf("month")
            .day();
        const lastOfMonth = moment(currentMoment)
            .endOf("month")
            .day();
        const firstDayOfGrid = moment(currentMoment)
            .startOf("month")
            .subtract(firstOfMonth, "days");
        const lastDayOfGrid = moment(currentMoment)
            .endOf("month")
            .subtract(lastOfMonth, "days")
            .add(6, "days");
        const startCalendar = firstDayOfGrid.date();
        const endCalendar = startCalendar + lastDayOfGrid.diff(firstDayOfGrid, "days");

        for (let date = startCalendar; date <= endCalendar; date++)
        {
            const newDate = moment(firstDayOfGrid).date(date);
            if (this.displayedDates.length < 42)
                this.displayedDates.push(newDate.toDate());
        }

        this.setDisplayedWeeks();
    }

    setDisplayedWeeks(): void
    {
        const weeks = [];
        const dates = Utilities.deepCopy(this.displayedDates);
        while (dates.length > 0)
        {
            weeks.push(dates.splice(0, 7));
        }
        this.displayedWeeks = weeks;
    }

    setCurrentMonthDates(): void
    {
        // CLOUDIV-30423: Clear caches when dates change
        this.firstLastClassCache.clear();

        if (!this.model)
        {
            this.currentMonthDates = [];
            this.orderedEvents = {};
            this.eventByIdCache = new Map();
            return;
        }

        // CLOUDIV-30423: Build event ID cache ONCE for O(1) click lookups
        // Avoids O(n) .find() through 27,356 dates on every click
        this.eventByIdCache = new Map();
        const fullModel = this.metadataModel?.[this.propertyMetadata?.Name];
        if (fullModel && Array.isArray(fullModel))
        {
            for (const event of fullModel)
            {
                if (event.Id)
                {
                    this.eventByIdCache.set(event.Id, event);
                }
            }
        }

        // CLOUDIV-30423: Build Set of displayed date strings ONCE for O(1) lookup instead of O(m) .find()
        // This avoids 27,356 * 35 = ~957,000 comparisons and Date object allocations
        const displayedDateStrings = new Set<string>();
        for (const dt of this.displayedDates)
        {
            displayedDateStrings.add(dt.toDateString());
        }

        // O(n) filter with O(1) Set lookup instead of O(n*m) filter with O(m) .find()
        const filtered = this.model.filter(x => displayedDateStrings.has(new Date(x.Day).toDateString())) as Array<IOrderedCalendarDate>;
        this.currentMonthDates = Utilities.deepCopy(filtered);

        if (this.currentMonthDates)
        {
            // CLOUDIV-30423: Build lookup map ONCE instead of filtering 16,772 dates for each month date
            const previousDayDate = moment(this.displayedDates[0]).add(-1, "d")
                .toDate()
                .toDateString();
            const previousDayLookup = new Set<string>();
            for (const model of this.model)
            {
                if (new Date(model.Day).toDateString() === previousDayDate)
                {
                    previousDayLookup.add(model.DateKey);
                }
            }

            // CLOUDIV-30423: Build range lookup map for setFirstLastInRange optimization
            const rangeMap = new Map<string, boolean>();
            for (const date of this.currentMonthDates)
            {
                const dateStr = new Date(date.Day).toDateString();
                const key1 = `${dateStr}|${date.DateKey}|${date.Color}|${date.DateDetailKey || ''}`;
                const key2 = `${dateStr}|${date.DateKey}|${date.Color}`;
                rangeMap.set(key1, true);
                rangeMap.set(key2, true);
            }

            this.currentMonthDates.forEach(x =>
            {
                // O(1) lookup instead of O(n) filter
                const previousDay = previousDayLookup.has(x.DateKey);

                if (previousDay)
                {
                    x["$previousDay"] = true;
                }
                x["$stringDate"] = new Date(x.Day).toDateString();

                this.setFirstLastInRangeOptimized(x, "first", rangeMap);
                this.setFirstLastInRangeOptimized(x, "last", rangeMap);
            });

            this.setMultipleDatesDictionary();
        }
        if (this.displayedDates?.length > 0 && this.currentMonthDates?.length > 0)
            this.setOrderedEvents();
    }

    // CLOUDIV-30423: Optimized version using Map lookup instead of filter
    setFirstLastInRangeOptimized(calendarDate: ICalendarDate, firstLast: string, rangeMap: Map<string, boolean>): void
    {
        // FIXED: Negate offset to match original logic
        // "first" checks day BEFORE (offset -1), "last" checks day AFTER (offset +1)
        const offset: number = firstLast === "first" ? -1 : 1;
        const adjacentDate = new Date(calendarDate.Day);
        adjacentDate.setDate(adjacentDate.getDate() + offset);
        const adjacentDateStr = adjacentDate.toDateString();

        let keyToCheck: string;
        if (calendarDate.ShowDetail)
        {
            keyToCheck = `${adjacentDateStr}|${calendarDate.DateKey}|${calendarDate.Color}|${calendarDate.DateDetailKey || ''}`;
        } else
        {
            keyToCheck = `${adjacentDateStr}|${calendarDate.DateKey}|${calendarDate.Color}`;
        }

        // If adjacent date NOT found in map, mark as first/last in range
        if (!rangeMap.has(keyToCheck))
        {
            calendarDate[`$${firstLast}InRange`] = true;
        }
    }

    setFirstLastInRange(calendarDate: ICalendarDate, firstLast: string): void
    {
        const offset: number = firstLast === "first" ? 1 : -1;
        const match = this.currentMonthDates.filter(
            x =>
            {
                const date = new Date(x.Day);
                date.setDate(date.getDate() + offset);
                if (calendarDate.ShowDetail)
                    return x.DateKey === calendarDate.DateKey && new Date(date).toDateString() === new Date(calendarDate.Day).toDateString() && calendarDate.Color === x.Color && calendarDate.DateDetailKey === x.DateDetailKey;
                else
                    return x.DateKey === calendarDate.DateKey && new Date(date).toDateString() === new Date(calendarDate.Day).toDateString() && calendarDate.Color === x.Color;
            }
        ).length === 0;
        if (match)
            calendarDate[`$${firstLast}InRange`] = true;
    }

    multiple: Record<string, boolean> = {};

    setMultipleDatesDictionary(): void
    {
        const self = this;
        this.currentMonthDates.forEach(x =>
        {
            const date = x["$stringDate"];
            self.multiple[date] = !Utilities.IsNullOrUndefined(self.multiple[date]);
        });
    }

    showDay(day: Date): boolean
    {
        if (!this.displayedDates)
            return false;
        return day.getMonth() === this.displayedDates[15].getMonth();
    }

    showTooltip(day): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.tooltip(day));
    }

    tooltip(day: Date): string
    {
        if (!this.showDay(day))
            return null;
        if (!this.orderedEvents)
            return null;
        const events = this.orderedEvents[day.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
            return null;
        return this.multiple[day["$stringDate"]] ? "Multiple events" : events[0].DateKey;
    }

    background(day: ICalendarDate): string
    {
        return this.multiple[day["$stringDate"]] ? "multiple" : day.Color;
    }

    dateRangeClass(day: Date): string
    {
        let ret = "";
        if (!this.showDay(day))
            return null;
        if (!this.orderedEvents)
            return null;
        const events = this.orderedEvents[day.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
        {
            ret += this.lastDayOfRange;
            ret += ` ${this.firstDayOfRange}`;
            return ret;
        }
        if (events[0].Selected)
        {
            if (this.firstLastClass(events[0], 1))
                ret += this.lastDayOfRange;
            if (this.firstLastClass(events[0], -1))
                ret += ` ${this.firstDayOfRange}`;
        }

        ret += ` event-color-${this.background(events[0])}`;

        switch (day.getDay())
        {
            case 0:
                ret += ` first-day-of-week`;
                break;
            case 6:
                ret += ` last-day-of-week`;
                break;
        }

        return ret;
    }

    // CLOUDIV-30423: Cache for firstLastClass lookups to avoid repeated filters
    private firstLastClassCache = new Map<string, boolean>();

    firstLastClass(day: ICalendarDate, add: number): boolean
    {
        // Build cache key
        const cacheKey = `${day["$stringDate"]}|${day.Color}|${add}`;

        // Check cache first
        if (this.firstLastClassCache.has(cacheKey))
        {
            return this.firstLastClassCache.get(cacheKey);
        }

        const dd = new Date(day.Day);
        const s = new Date(dd.setDate(dd.getDate() + add)).toDateString();

        // Use .some() instead of .filter().length for early exit optimization
        const result = !this.currentMonthDates.some(x => x["$stringDate"] === s && day.Color === x.Color);

        // Cache the result
        this.firstLastClassCache.set(cacheKey, result);

        return result;
    }

    drillIn(date?: any): void
    {
        this.toggleDrillIn.emit(<MetadataCalendarToggleParameter>{ date: date });
    }

    changeMonthText(val): string
    {
        return moment(this.startDate).add(val, "M")
            .format("MMMM YYYY");
    }

    changeMonth(val: moment.DurationInputArg1): void
    {
        this.startDate = moment(this.calDate)
            .add(val, "M")
            .toDate();
        this.setCalDate();
        this.setDisplayedDates(this.calDate);
        this.setCurrentMonthDates();
    }

    setCalDate(): void
    {
        const month = new Date(this.startDate);
        month.setMonth(this.startDate.getMonth());
        this.calDate = moment(month).toDate();
    }

    onKeyDown(event: KeyboardEvent, day: Date): void
    {
        if (event.key === "Enter" || event.key === " ")
        {
            this.onClick(day);
        }
    }

    onClick(date: Date): void
    {
        if (!this.orderedEvents)
            return;
        const events = this.orderedEvents[date.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
            return;
        if (this.multiple[new Date(date).toDateString()])
        {
            this.drillIn(date);
            return;
        }
        const self = this;
        // CLOUDIV-30423: Use O(1) Map lookup instead of O(n) .find() through 27,356 dates
        const clickedEvent = this.eventByIdCache?.get(events[0].Id);
        if (clickedEvent)
            clickedEvent.Clicked = true;

        let type = this.metadataModel.$$TypeName;
        if (!type && this.formRepository) type = this.formRepository.Type;
        this.$metadataPropertyService.recalculateProperties(
            type,
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            this.metadataModel[this.propertyMetadata.Name],
            undefined,
            true,
            true,
            this.formRepository
        ).then(_x =>
        {
            // CLOUDIV-30423: Only clear Clicked flag on the one event that was clicked, not all 27,356
            if (clickedEvent)
            {
                clickedEvent.Clicked = false;
            }
        });
    }
}
